import { pipe } from 'fp-ts/function';
import { useEffect } from 'react';
import { connect, useDispatch } from 'react-redux';
import { array } from 'fp-ts';
import { DataTestId } from '@mews-commander/enums';
import { TimeUnit } from '@mews/enums';
import { useAppClockService, withAppClockService } from '@mews/framework';
import { Container, Stack, StackItem, withBreakpointRange } from '@optimus-web/core';
import { ErrorHandler, OverlayedLoader, isMobileOrTablet } from '@mews-commander/ui';
import {
    setCategories,
    setProfiles,
    setRateGroups,
    setRates,
    setResourceCategories,
} from '@mews-commander/legacy-entities/src/ducks';
import { useCommanderSelector } from '@mews-commander/utils';
import { changeFilterValues, closeSidebar, resetCellSelection } from '../actions';
import { RESTRICTIONS_FILTERS_FORM_NAME } from '../constants';
import { useCalendarRestrictions } from '../queries';
import { getLocalizedDefaultTimeUnitRange, getSelectedCell } from '../selectors';
import RestrictionsSidebar from './sidebar';
import RestrictionsFilters from './RestrictionsFilters';
import RestrictionsMatrix from './matrix';

const Restrictions = ({ breakpointRange, resetToInitialState, serviceId }: $TSFixMe) => {
    const appClockService = useAppClockService();
    const dispatch = useDispatch();
    const isMobileDevice = isMobileOrTablet(breakpointRange);

    const initialFormValues = useCommanderSelector((state) =>
        getLocalizedDefaultTimeUnitRange(state, { timeUnit: TimeUnit.Day, appClockService })
    );
    const result = useCalendarRestrictions(serviceId);

    useEffect(() => resetToInitialState, [resetToInitialState]);

    const selectedCell = useCommanderSelector((state) => getSelectedCell(state, { serviceId, appClockService }));

    useEffect(() => {
        if (result.isSuccess) {
            const { rateGroups, rates, resourceCategories, profiles } = result.data.entities;
            if (rates && array.isNonEmpty(rates)) {
                dispatch(setRates(rates, serviceId));
            }
            if (rateGroups && array.isNonEmpty(rateGroups)) {
                dispatch(setRateGroups(rateGroups, serviceId));
            }
            if (resourceCategories && array.isNonEmpty(resourceCategories)) {
                dispatch(setResourceCategories(resourceCategories, serviceId));
                dispatch(setCategories(resourceCategories, serviceId));
            }
            if (profiles && array.isNonEmpty(profiles)) {
                dispatch(setProfiles(profiles));
            }
        }
    }, [dispatch, result.isSuccess, result.data?.entities, serviceId]);

    return (
        <Stack fill vertical>
            <Container
                padding="200"
                data-test-id={DataTestId.RestrictionCalendarCard}
                tabletMax={{
                    padding: '100',
                }}
            >
                <Stack spacing="200" vertical>
                    <RestrictionsFilters
                        form={RESTRICTIONS_FILTERS_FORM_NAME}
                        initialValues={initialFormValues}
                        // @ts-expect-error
                        serviceId={serviceId}
                    />
                </Stack>
                {(result.isLoading || result.isFetching) && <OverlayedLoader show />}
                {result.isError && <ErrorHandler />}
                {result.isSuccess && (
                    <Stack vertical>
                        <StackItem padding="200">
                            <RestrictionsMatrix
                                restrictionCalendarRows={result.data.rows}
                                isMobile={isMobileDevice}
                                serviceId={serviceId}
                            />
                            {selectedCell.rowIndex !== null && selectedCell.columnIndex !== null ? (
                                <RestrictionsSidebar
                                    serviceId={serviceId}
                                    rowCondition={result.data.rows[selectedCell.rowIndex].conditions}
                                    restrictions={
                                        result.data.rows[selectedCell.rowIndex].restrictions[selectedCell.columnIndex]
                                    }
                                    disableClickAway
                                />
                            ) : null}
                        </StackItem>
                    </Stack>
                )}
            </Container>
        </Stack>
    );
};

export default pipe(
    Restrictions,
    withBreakpointRange,
    connect(
        null,
        // @ts-expect-error $TSFixMeError: Property 'serviceId' does not exist on type '{}'.
        (dispatch, { serviceId }) => ({
            resetToInitialState: () => {
                // @ts-expect-error $TSFixMeError: Argument of type '(dispatch: $TSFixMe, getState: $TSFixMe) => void' is not assignable to parameter of type 'Action<any>'...
                dispatch(resetCellSelection(serviceId));

                // @ts-expect-error $TSFixMeError: Argument of type '(dispatch: $TSFixMe, getState: $TSFixMe) => void' is not assignable to parameter of type 'Action<any>'...
                dispatch(closeSidebar(serviceId));
                dispatch(changeFilterValues({ dateRange: null }, serviceId));
            },
        })
    ),
    withAppClockService
);
